class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
    window.cartDrawer = this;
  }

  setHeaderCartIconAccessibility() {
    // Header cart icon navigates to the cart page (do not open drawer).
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.removeAttribute('role');
    cartLink.removeAttribute('aria-haspopup');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.removeTrustBadges();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  removeTrustBadges() {
    this.querySelectorAll(
      '.cart-drawer__trust-badges, [class*="cart-drawer__trust"], [class*="trust-badge"]'
    ).forEach((el) => el.remove());
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.classList.remove('is-empty');

    this.productId = parsedState.id;

    const applySections = (sections) => {
      if (!sections) return;
      this.getSectionsToRender().forEach((section) => {
        const sectionHtml = sections[section.id];
        if (!sectionHtml) return;

        const elementToReplace = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!elementToReplace) return;

        const html = this.getSectionInnerHTML(sectionHtml, section.selector);
        if (html != null) elementToReplace.innerHTML = html;
      });
      this.removeTrustBadges();
    };

    const openDrawer = () => {
      this.removeTrustBadges();
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    };

    if (parsedState.sections) {
      applySections(parsedState.sections);
      setTimeout(openDrawer);
      return;
    }

    // Fallback: refresh drawer HTML then open
    const sectionIds = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');
    fetch(`${window.Shopify?.routes?.root || '/'}?sections=${sectionIds}`)
      .then((r) => r.json())
      .then((sections) => {
        applySections(sections);
        openDrawer();
      })
      .catch(() => {
        openDrawer();
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    parsed.querySelectorAll('.cart-drawer__trust-badges, [class*="cart-drawer__trust"]').forEach((el) =>
      el.remove()
    );
    const el = parsed.querySelector(selector) || parsed.querySelector('.shopify-section');
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // cart-discount already re-renders drawer sections — avoid a second stale refresh race
      if (
        !event?.source ||
        event.source === 'cart-items' ||
        event.source === 'cart-discount' ||
        event.source === 'cart-discount-clear'
      ) {
        return;
      }
      this.onCartUpdate();
    });
  }

  onCartUpdate() {
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}?sections=cart-drawer`)
      .then((response) => response.json())
      .then((sections) => {
        const html = sections?.['cart-drawer'];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
          const current = document.querySelector(selector);
          const next = doc.querySelector(selector);
          if (current && next) current.replaceWith(next);
        });
      })
      .catch((error) => console.error(error));
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Cart coupon offers: delegated handlers (inline scripts do not run after drawer AJAX re-render)
(function () {
  if (window.__cartCouponOffersDelegated) return;
  window.__cartCouponOffersDelegated = true;

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        if (document.execCommand('copy')) resolve();
        else reject(new Error('copy failed'));
      } catch (err) {
        reject(err);
      } finally {
        document.body.removeChild(ta);
      }
    });
  }

  function normalizeCouponCode(code) {
    return String(code || '')
      .trim()
      .replace(/^#/, '');
  }

  function applyCouponFromButton(codeButton, code) {
    var scope = codeButton.closest('.drawer__footer, .cart__blocks, .cart__footer') || document;
    var discountRoot =
      scope.querySelector('cart-discount') ||
      document.querySelector('cart-drawer cart-discount') ||
      document.querySelector('cart-discount');
    var discountInput =
      (discountRoot && discountRoot.querySelector('.cart__discount-input')) ||
      scope.querySelector('.cart__discount-input') ||
      document.querySelector('#CartDrawer-discount, #Cart-discount');

    var cleanCode = normalizeCouponCode(code);
    if (discountInput) {
      discountInput.value = cleanCode;
      discountInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if (discountRoot && typeof discountRoot.onApply === 'function') {
      discountRoot.onApply(cleanCode);
    }
  }

  function isCartCouponCodeButton(target) {
    return (
      target.closest('.cart-coupon-offers .product-page-discounts__code') ||
      target.closest('product-page-discounts.product-page-discounts--cart .product-page-discounts__code')
    );
  }

  function isCartCouponToggle(target) {
    return (
      target.closest('.cart-coupon-offers [data-action="toggle-discounts"]') ||
      target.closest('product-page-discounts.product-page-discounts--cart [data-action="toggle-discounts"]')
    );
  }

  document.addEventListener('click', function (event) {
    var toggle = isCartCouponToggle(event.target);
    if (toggle) {
      event.preventDefault();
      var root =
        toggle.closest('.cart-coupon-offers') || toggle.closest('product-page-discounts');
      var list = root && root.querySelector('.product-page-discounts__list');
      if (!list) return;

      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      var next = !expanded;
      toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
      list.setAttribute('data-collapsed', next ? 'false' : 'true');

      var viewLabel = toggle.querySelector('[data-toggle-label="view"]');
      var hideLabel = toggle.querySelector('[data-toggle-label="hide"]');
      if (viewLabel) viewLabel.hidden = next;
      if (hideLabel) hideLabel.hidden = !next;
      return;
    }

    var codeButton = isCartCouponCodeButton(event.target);
    if (!codeButton) return;

    event.preventDefault();
    var code = codeButton.getAttribute('data-code');
    if (!code) return;

    // Mark handled so product-page-discounts inline script does not also treat this click
    event.__cartCouponHandled = true;

    copyText(normalizeCouponCode(code))
      .then(function () {
        codeButton.classList.add('is-copied');
        applyCouponFromButton(codeButton, code);
        window.setTimeout(function () {
          codeButton.classList.remove('is-copied');
        }, 1800);
      })
      .catch(function () {
        // Still apply even if clipboard is blocked
        applyCouponFromButton(codeButton, code);
      });
  });
})();
